import { 
  Container, 
  Title, 
  Text, 
  Grid, 
  Loader, 
  Alert, 
  Button, 
  Card, 
  Group, 
  Stack,
  ThemeIcon,
  Box,
  Paper,
  Badge
} from '@mantine/core';
import { 
  IconTruck, 
  IconShieldCheck, 
  IconClock, 
  IconHeart,
  IconStar,
  IconPackage,
  IconArrowRight
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useProducts } from '@/lib/hooks';
import { ProductCard } from '@/components/ProductCard';
import { useLanguage } from '@/context/LanguageContext';
import { useSEO } from '@/lib/useSEO';

export default function HomePage() {
  const { t } = useLanguage();
  const { data, isLoading, error } = useProducts();
  const products = data?.products ?? [];

  // SEO for homepage
  useSEO({
    title: t('seo.home.title') || 'Luxelo - Premium LED Lighting Solutions',
    description: t('seo.home.description') || 'Discover premium LED lighting products with fast InPost shipping and secure Stripe payments. Shop now for high-quality lighting solutions.',
    type: 'website',
  });

  if (isLoading) {
    return (
      <Container py="xl">
        <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container py="xl">
        <Alert color="red" title="Error">
          Failed to load products
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <Box
        style={{
          position: 'relative',
          color: 'white',
          padding: '3rem 0',
          overflow: 'hidden',
        }}
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source
            src="https://firebasestorage.googleapis.com/v0/b/luxelo.firebasestorage.app/o/public%2Fhero_banner_3_sm.webm?alt=media&token=bc799282-a86e-48a5-a976-ec9737b35dd9"
            type="video/webm"
          />
        </video>
        
        {/* Dark Overlay */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1,
          }}
        />
        
        {/* Content */}
        <Container size="xl" style={{ position: 'relative', zIndex: 2 }}>
          <Stack align="flex-start" gap="xl" style={{ maxWidth: '800px' }}>
            <Badge size="lg" variant="light" color="white" style={{ color: '#667eea' }}>
              {t('homepage.hero.badge')}
            </Badge>
            <Title 
              order={1} 
              size="3.5rem" 
              ta="left"
              style={{ 
                fontWeight: 300,
                lineHeight: 1.2,
              }}
            >
              {t('homepage.hero.title')}
            </Title>
            <Text 
              size="xl" 
              ta="left" 
              style={{ 
                maxWidth: '600px',
                opacity: 0.95
              }}
            >
              {t('homepage.hero.subtitle')}
            </Text>
            <Group gap="md" mt="md">
              <Button 
                size="lg" 
                variant="white" 
                color="dark"
                component={Link}
                to="#products"
                rightSection={<IconArrowRight size={20} />}
              >
                {t('homepage.hero.shopNow')}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                color="white"
                component={Link}
                to="/about-us"
              >
                {t('homepage.hero.learnMore')}
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Container size="xl" py={80}>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" h="100%">
              <Stack align="center" gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="blue">
                  <IconTruck size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" ta="center">
                  {t('homepage.features.shipping.title')}
                </Title>
                <Text size="sm" c="dimmed" ta="center">
                  {t('homepage.features.shipping.description')}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" h="100%">
              <Stack align="center" gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="green">
                  <IconShieldCheck size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" ta="center">
                  {t('homepage.features.quality.title')}
                </Title>
                <Text size="sm" c="dimmed" ta="center">
                  {t('homepage.features.quality.description')}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" h="100%">
              <Stack align="center" gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="orange">
                  <IconClock size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" ta="center">
                  {t('homepage.features.support.title')}
                </Title>
                <Text size="sm" c="dimmed" ta="center">
                  {t('homepage.features.support.description')}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" h="100%">
              <Stack align="center" gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="red">
                  <IconHeart size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" ta="center">
                  {t('homepage.features.satisfaction.title')}
                </Title>
                <Text size="sm" c="dimmed" ta="center">
                  {t('homepage.features.satisfaction.description')}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box style={{ backgroundColor: '#f8f9fa' }} py={80}>
        <Container size="xl">
          <Stack align="center" gap="xl" mb={60}>
            <Title order={2} size="2.5rem" ta="center">
              {t('homepage.whyChoose.title')}
            </Title>
            <Text size="lg" c="dimmed" ta="center" style={{ maxWidth: '700px' }}>
              {t('homepage.whyChoose.subtitle')}
            </Text>
          </Stack>

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper shadow="xs" p="xl" radius="md" h="100%">
                <Group align="flex-start" gap="lg">
                  <ThemeIcon size={50} radius="md" color="violet">
                    <IconStar size={26} />
                  </ThemeIcon>
                  <Stack gap="sm" style={{ flex: 1 }}>
                    <Title order={3} size="h4">
                      {t('homepage.whyChoose.premium.title')}
                    </Title>
                    <Text c="dimmed">
                      {t('homepage.whyChoose.premium.description')}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper shadow="xs" p="xl" radius="md" h="100%">
                <Group align="flex-start" gap="lg">
                  <ThemeIcon size={50} radius="md" color="indigo">
                    <IconPackage size={26} />
                  </ThemeIcon>
                  <Stack gap="sm" style={{ flex: 1 }}>
                    <Title order={3} size="h4">
                      {t('homepage.whyChoose.care.title')}
                    </Title>
                    <Text c="dimmed">
                      {t('homepage.whyChoose.care.description')}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Products Section */}
      <Container size="xl" py={80} id="products">
        <Stack align="center" gap="xl" mb={60}>
          <Title order={2} size="2.5rem" ta="center">
            {t('homepage.products.title')}
          </Title>
          <Text size="lg" c="dimmed" ta="center" style={{ maxWidth: '700px' }}>
            {t('homepage.products.subtitle')}
          </Text>
        </Stack>

        {products.length === 0 ? (
          <Text ta="center" py="xl" c="dimmed">
            {t('homepage.products.empty')}
          </Text>
        ) : (
          <Grid>
            {products.map(product => (
              <Grid.Col key={product.id} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
                <ProductCard product={product} />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Container>

      {/* CTA Section */}
      <Box
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '5rem 0',
        }}
      >
        <Container size="md">
          <Stack align="center" gap="xl">
            <Title order={2} size="2.5rem" ta="center">
              {t('homepage.cta.title')}
            </Title>
            <Text size="lg" ta="center" style={{ opacity: 0.95 }}>
              {t('homepage.cta.subtitle')}
            </Text>
            <Group gap="md">
              <Button 
                size="lg" 
                variant="white" 
                color="dark"
                component={Link}
                to="/contact"
              >
                {t('homepage.cta.contact')}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                color="white"
                component={Link}
                to="/faq"
              >
                {t('homepage.cta.faq')}
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>
    </>
  );
}

